import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { PATH_MAP } from "../../configuration/PathMap"
import { AdminDashboardPage, AdminDashboardSection } from "../AdminDashboard/AdminDashboardPage"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import logo from "../../assets/images/Logo.png"
import {
  faChartLine,
  faClipboardList,
  faCoins,
  faHandshake,
  faLifeRing,
  faPowerOff,
  faServer,
  faFileLines,
  faAddressBook,
  faMoneyBillTrendUp,
  faMagnifyingGlass,
  faShop,
  faRobot,
  faBell,
  faLayerGroup,
  faTicket,
  faUserShield,
  faChevronLeft,
  faChevronRight,
  faUserTie,
  faPercent,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { useAdminEmployeeLogged } from "../../context/useAdminEmployeeContext"
import { AdminPermissions, AdminPermissionsType } from "../../Enum/AdminPermissionsType"
import { DEFAULT_ADMIN_AVATAR } from "../../utils/employeeAvatar"

const sectionOptions: {
  key: AdminDashboardSection
  labelKey: string
  descriptionKey: string
  icon: IconDefinition
  permission?: AdminPermissionsType
  isSubItem?: boolean
}[] = [
  {
    key: "overview",
    labelKey: "admin_dashboard.sections.overview.label",
    descriptionKey: "admin_dashboard.sections.overview.description",
    icon: faChartLine,
    permission: AdminPermissions.CanViewOverview,
  },
  {
    key: "subscriptions",
    labelKey: "admin_dashboard.sections.subscriptions.label",
    descriptionKey: "admin_dashboard.sections.subscriptions.description",
    icon: faClipboardList,
    permission: AdminPermissions.CanManageSubscriptions,
  },
  {
    key: "plans",
    labelKey: "admin_dashboard.sections.plans.label",
    descriptionKey: "admin_dashboard.sections.plans.description",
    icon: faLayerGroup,
    permission: AdminPermissions.CanManagePlans,
  },
  {
    key: "plan_coupons",
    labelKey: "admin_dashboard.sections.plan_coupons.label",
    descriptionKey: "admin_dashboard.sections.plan_coupons.description",
    icon: faTicket,
    permission: AdminPermissions.CanManagePlanCoupons,
  },
  {
    key: "platform_whatsapp",
    labelKey: "admin_dashboard.sections.platform_whatsapp.label",
    descriptionKey: "admin_dashboard.sections.platform_whatsapp.description",
    icon: faRobot,
    permission: AdminPermissions.CanManagePlatformWhatsApp,
  },
  {
    key: "platform_notices",
    labelKey: "admin_dashboard.sections.platform_notices.label",
    descriptionKey: "admin_dashboard.sections.platform_notices.description",
    icon: faBell,
    permission: AdminPermissions.CanManagePlatformNotices,
  },
  {
    key: "commercial_performance",
    labelKey: "admin_dashboard.sections.commercial_performance.label",
    descriptionKey: "admin_dashboard.sections.commercial_performance.description",
    icon: faHandshake,
    permission: AdminPermissions.CanViewOverview,
  },
  {
    key: "financial_metrics",
    labelKey: "admin_dashboard.sections.financial_metrics.label",
    descriptionKey: "admin_dashboard.sections.financial_metrics.description",
    icon: faCoins,
    permission: AdminPermissions.CanViewFinancialMetrics,
  },
  {
    key: "store_financial_health",
    labelKey: "admin_dashboard.sections.store_financial_health.label",
    descriptionKey: "admin_dashboard.sections.store_financial_health.description",
    icon: faShop,
    permission: AdminPermissions.CanViewFinancialMetrics,
  },
  {
    key: "mercado_pago_queries",
    labelKey: "admin_dashboard.sections.mercado_pago_queries.label",
    descriptionKey: "admin_dashboard.sections.mercado_pago_queries.description",
    icon: faMagnifyingGlass,
    permission: AdminPermissions.CanQueryMercadoPago,
  },
  {
    key: "growth_investments",
    labelKey: "admin_dashboard.sections.growth_investments.label",
    descriptionKey: "admin_dashboard.sections.growth_investments.description",
    icon: faMoneyBillTrendUp,
    permission: AdminPermissions.CanManageGrowthInvestments,
  },
  {
    key: "tutorials",
    labelKey: "admin_dashboard.sections.tutorials.label",
    descriptionKey: "admin_dashboard.sections.tutorials.description",
    icon: faLifeRing,
    permission: AdminPermissions.CanManageTutorials,
  },
  {
    key: "server_runtime",
    labelKey: "admin_dashboard.sections.server_runtime.label",
    descriptionKey: "admin_dashboard.sections.server_runtime.description",
    icon: faServer,
    permission: AdminPermissions.CanViewServerRuntime,
  },
  {
    key: "landing_leads",
    labelKey: "admin_dashboard.sections.landing_leads.label",
    descriptionKey: "admin_dashboard.sections.landing_leads.description",
    icon: faAddressBook,
    permission: AdminPermissions.CanViewLandingLeads,
  },
  {
    key: "server_logs",
    labelKey: "admin_dashboard.sections.server_logs.label",
    descriptionKey: "admin_dashboard.sections.server_logs.description",
    icon: faFileLines,
    permission: AdminPermissions.CanManageServerLogs,
  },
  {
    key: "resellers",
    labelKey: "admin_dashboard.sections.resellers.label",
    descriptionKey: "admin_dashboard.sections.resellers.description",
    icon: faUserTie,
    permission: AdminPermissions.CanManageResellers,
  },
  {
    key: "reseller_commission_plans",
    labelKey: "admin_dashboard.sections.reseller_commission_plans.label",
    descriptionKey: "admin_dashboard.sections.reseller_commission_plans.description",
    icon: faPercent,
    permission: AdminPermissions.CanManageResellers,
    isSubItem: true,
  },
  {
    key: "admin_staff",
    labelKey: "admin_dashboard.sections.admin_staff.label",
    descriptionKey: "admin_dashboard.sections.admin_staff.description",
    icon: faUserShield,
  },
]

const isValidSection = (value: string | null): value is AdminDashboardSection =>
  sectionOptions.some((item) => item.key === value)

const ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY = "admin_sidebar_collapsed"

export const AdminDashboardScreen: React.FC = () => {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()

  const { t } = useTranslation("frontend")

  const sectionFromUrl = searchParams.get("section")

  const section: AdminDashboardSection = isValidSection(sectionFromUrl) ? sectionFromUrl : "overview"

  useEffect(() => {
    if (isValidSection(sectionFromUrl)) return

    setSearchParams({ section: "overview" }, { replace: true })
  }, [sectionFromUrl, setSearchParams])

  const { adminEmployeeProfile, setAdminEmployeeProfile } = useAdminEmployeeLogged()

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY) === "1",
  )

  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY, next ? "1" : "0")
      return next
    })
  }

  const handleLogout = () => {
    setIsLoggingOut(true)
    
    localStorage.removeItem(API_ROUTES.TOKEN_ADMIN)
    
    setAdminEmployeeProfile(null)
    
    navigate(PATH_MAP.ADMIN_LOGIN_PAGE, { replace: true })
  }

  const canViewSection = useCallback((option: (typeof sectionOptions)[number]) => {
    if (option.key === "admin_staff") return adminEmployeeProfile?.isRoot === true

    if (adminEmployeeProfile?.isRoot) return true

    return option.permission ? (adminEmployeeProfile?.permissions.includes(option.permission) ?? false) : true
  }, [adminEmployeeProfile?.isRoot, adminEmployeeProfile?.permissions])

  const visibleSectionOptions = useMemo(() => sectionOptions.filter(canViewSection), [canViewSection])

  useEffect(() => {
    const currentOption = sectionOptions.find((item) => item.key === section)

    if (currentOption && !canViewSection(currentOption)) {
      const fallback = visibleSectionOptions[0]?.key ?? "overview"

      setSearchParams({ section: fallback }, { replace: true })
    }
  }, [canViewSection, section, setSearchParams, visibleSectionOptions])

  const handleSectionChange = (nextSection: AdminDashboardSection) => {
    setSearchParams({ section: nextSection })
  }

  if (isLoggingOut) {
    return null
  }

  const requestedSectionOption = sectionOptions.find((item) => item.key === section)

  const effectiveSection: AdminDashboardSection =
    requestedSectionOption && canViewSection(requestedSectionOption)
      ? section
      : (visibleSectionOptions[0]?.key ?? "overview")

  const currentSection = sectionOptions.find((item) => item.key === effectiveSection)

  const isViewportConstrainedSection = effectiveSection === "server_logs" || effectiveSection === "platform_whatsapp"

  return (
    <div className="bg-white md:flex">
      <aside
        className={`sticky top-0 z-30 hidden h-dvh shrink-0 flex-col border-r border-slate-200 bg-white md:flex ${
          isSidebarCollapsed ? "md:w-16" : "md:w-64"
        }`}
      >
        <div
          className={`flex items-center gap-2 border-b border-slate-200 px-3 py-4 ${
            isSidebarCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <img src={logo} className="w-10 shrink-0" alt="Logo" />
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">
                {t("admin_dashboard.admin")}
              </span>
            </div>
          )}

          <button
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            onClick={handleToggleSidebar}
            title={t(isSidebarCollapsed ? "admin_dashboard.expand_sidebar" : "admin_dashboard.collapse_sidebar")}
            data-interaction-description-key="interactionDescriptions:pages.admin.adminDashboardScreen.button.handleToggleSidebar"
          >
            <FontAwesomeIcon icon={isSidebarCollapsed ? faChevronRight : faChevronLeft} className="text-xs" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          <ul className="flex flex-col gap-1">
            {visibleSectionOptions.map((item) => (
              <li key={item.key}>
                <button
                  className={`flex w-full items-center gap-3 rounded-lg py-2.5 transition ${
                    item.isSubItem && !isSidebarCollapsed ? "pl-8 pr-3 text-xs font-semibold" : "px-3 text-sm font-bold"
                  } ${isSidebarCollapsed ? "justify-center" : ""} ${
                    section === item.key
                      ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow"
                      : "text-slate-600 hover:bg-orange-50 hover:text-orange-700"
                  }`}
                  onClick={() => handleSectionChange(item.key)}
                  title={isSidebarCollapsed ? t(item.labelKey) : undefined}
                  data-interaction-description-key="interactionDescriptions:pages.admin.adminDashboardScreen.button.handleSectionChangeItemKey"
                >
                  <FontAwesomeIcon icon={item.icon} className="shrink-0 text-sm" />

                  {!isSidebarCollapsed && <span className="min-w-0 flex-1 truncate text-left">{t(item.labelKey)}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-slate-200 p-2">
          <button
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-red-700 transition hover:bg-red-50 ${
              isSidebarCollapsed ? "justify-center" : ""
            }`}
            onClick={handleLogout}
            title={isSidebarCollapsed ? t("admin_dashboard.logout") : undefined}
            data-interaction-description-key="interactionDescriptions:pages.admin.adminDashboardScreen.button.handleSidebarLogout"
          >
            <FontAwesomeIcon icon={faPowerOff} className="shrink-0 text-xs" />

            {!isSidebarCollapsed && (
              <span className="min-w-0 flex-1 truncate text-left">{t("admin_dashboard.logout")}</span>
            )}
          </button>
        </div>
      </aside>

      <div
        className={`min-w-0 flex-1 px-1 py-2 pb-32 md:px-4 md:pb-6 xl:px-6 2xl:px-8 ${
          isViewportConstrainedSection
            ? "min-h-screen lg:flex lg:h-dvh lg:min-h-0 lg:flex-col lg:overflow-hidden"
            : "min-h-screen"
        }`}
      >
        <header className="sticky top-0 z-10 shrink-0 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-xl md:px-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {currentSection && (
                <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-red-100 text-orange-600 md:flex">
                  <FontAwesomeIcon icon={currentSection.icon} />
                </span>
              )}

              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold text-slate-900 md:text-xl">
                  {currentSection ? t(currentSection.labelKey) : t("admin_dashboard.admin")}
                </h1>

                <p className="truncate text-xs text-slate-500 md:text-sm">
                  {currentSection ? t(currentSection.descriptionKey) : ""}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              {adminEmployeeProfile && (
                <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pl-1 pr-3 md:flex">
                  <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full bg-white">
                    <img src={DEFAULT_ADMIN_AVATAR} alt="" className="h-full w-full object-cover" />

                    {adminEmployeeProfile.isRoot && (
                      <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white ring-2 ring-white">
                        <FontAwesomeIcon icon={faUserShield} className="text-[7px]" />
                      </span>
                    )}
                  </span>

                  <span className="max-w-[140px] truncate text-sm font-bold text-slate-800">
                    {adminEmployeeProfile.name}
                  </span>
                </div>
              )}

              <button
                className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 md:hidden"
                onClick={handleLogout}
                data-interaction-description-key="interactionDescriptions:pages.admin.adminDashboardScreen.button.handleHeaderLogout"
              >
                <FontAwesomeIcon icon={faPowerOff} className="text-xs" />
                {t("admin_dashboard.logout")}
              </button>
            </div>
          </div>
        </header>

        <main
          className={`mt-2 md:mt-6 ${isViewportConstrainedSection ? "lg:min-h-0 lg:flex-1 lg:overflow-hidden" : ""}`}
        >
          <AdminDashboardPage section={effectiveSection} />
        </main>

        <nav className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-200 bg-white px-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] pt-3 shadow-[0_-8px_28px_rgba(15,23,42,0.10)] md:hidden">
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max items-center gap-2.5">
              {visibleSectionOptions.map((item) => {
                const isActive = section === item.key

                return (
                  <button
                    key={item.key}
                    className={`inline-flex min-h-12 items-center gap-2.5 rounded-2xl border px-4 py-2.5 text-sm font-extrabold tracking-[0.01em] transition ${
                      isActive
                        ? "border-transparent bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md"
                        : "border-slate-200 bg-white text-slate-700 shadow-sm"
                    }`}
                    onClick={() => handleSectionChange(item.key)}
                    data-interaction-description-key="interactionDescriptions:pages.admin.adminDashboardScreen.button.handleSectionChangeItemKey2"
                  >
                    <FontAwesomeIcon icon={item.icon} className="text-sm" />

                    <span className="whitespace-nowrap">{t(item.labelKey)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </nav>
      </div>
    </div>
  )
}
